// modules/tenant/CustomerAdminDashboard.tsx
// ---------------------------------------------------------------------------
// Customer Admin dashboard, backed by GET /api/v1/dashboard/customer.
//
// Every figure on this page comes from the API — there is no mock data left in
// this file. Field names mirror the API contract exactly, including
// `startingThisWeek` / `startingThisMonth` (campaigns, products and events have
// no created_at column in the schema, so these count records whose start_date
// falls in the period — the card subtitles say "starting" for that reason).
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../app/store";
import { Box, Divider, Chip } from "@mui/material";
import {
    TrendingUp,
    Users,
    Link as LinkIcon,
    Eye,
    Database,
    Clock,
    Activity,
    BarChart3,
    PieChart,
    UserCheck,
    FileText,
    AlertCircle,
    RefreshCw,
    TriangleAlert,
    Sparkles,
} from "lucide-react";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart as RechartsPieChart,
    Pie,
    Cell,
} from "recharts";
import { API_V1_BASE_URL } from "../../config/api";

interface CustomerAdminDashboardProps {
    tenant: any;
}

// ---- API contract ---------------------------------------------------------

interface CountWithDelta {
    total: number;
    startingThisWeek?: number;
    startingThisMonth?: number;
    newThisWeek?: number;
}

interface DashboardData {
    tenant: { id: string; name: string; status: string; maxUsers: number };
    generatedAt: string;
    stats: {
        campaigns: CountWithDelta;
        products: CountWithDelta;
        events: CountWithDelta;
        assets: CountWithDelta;
        users: { active: number; max: number; seatsRemaining: number; utilizationPct: number };
        guestLinks: { active: number; assetShares: number; totalActive: number; expiringIn7Days: number };
        views: { total: number; thisMonth: number; prevMonth: number; changePct: number | null };
        storage: { usedBytes: number; usedGb: number; quotaGb: number; usedPct: number };
        uploads: { failedLast7Days: number; pending: number };
    };
    engagement: Array<{ label: string; period: string; views: number; assets: number; ingestion: number }>;
    ingestionWeekly: Array<{ label: string; day: string; processed: number; failed: number; pending: number }>;
    assetStatus: Array<{ name: string; value: number }>;
    campaignStatus: Array<{ name: string; value: number }>;
    recentActivity: Array<{
        id: string;
        action: string;
        details: string;
        at: string;
        actorEmail: string;
        actorRole: string;
        actorName: string | null;
    }>;
    guestExpiring: Array<{
        name: string;
        email: string;
        context: string;
        expiresAt: string;
        accessCount: number;
        source: string;
    }>;
    billing: {
        plan: string | null;
        contractValue: number | null;
        outstanding: number | null;
        currency: string;
        renewalDate: string | null;
    } | null;
    alerts: Array<{ level: "info" | "warning" | "critical"; code: string; message: string }>;
}

// ---- helpers --------------------------------------------------------------

/** Stable colour per status label, so the same status keeps its colour. */
const STATUS_COLORS: Record<string, string> = {
    active: "#10B981",
    approved: "#10B981",
    completed: "#10B981",
    published: "#3B82F6",
    draft: "#F59E0B",
    pending: "#F59E0B",
    paused: "#EF4444",
    rejected: "#EF4444",
    invalid: "#EF4444",
    archived: "#6B7280",
    unspecified: "#9CA3AF",
};

const FALLBACK_PALETTE = ["#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#64748B"];

const colorFor = (name: string, index: number) =>
    STATUS_COLORS[String(name).toLowerCase()] ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];

const titleCase = (value: string) =>
    String(value || "")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (ch) => ch.toUpperCase());

/** Human-readable size from raw bytes. */
const formatBytes = (bytes: number) => {
    if (!bytes || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / 1024 ** i;
    return `${value >= 10 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
};

const formatMoney = (value: number | null, currency: string) => {
    if (value === null || value === undefined) return "—";
    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currency || "USD",
            maximumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${value}`;
    }
};

/** "12m ago" / "3h ago" / "2d ago" — falls back to a date past a week. */
const relativeTime = (iso: string) => {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
};

/** Days until expiry, floored at 0. */
const daysUntil = (iso: string) => {
    const diff = new Date(iso).getTime() - Date.now();
    if (Number.isNaN(diff)) return 0;
    return Math.max(0, Math.ceil(diff / 86400000));
};

const signed = (value: number) => (value > 0 ? `+${value}` : `${value}`);

// ---- stat card ------------------------------------------------------------

interface StatCardProps {
    icon: React.ReactNode;
    title: string;
    value: number | string;
    subtitle: string;
    color: string;
    trend?: string;
    trendUp?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, color, trend, trendUp = true }) => {
    const colorClasses: Record<string, string> = {
        blue: "border-blue-500",
        green: "border-green-500",
        red: "border-red-500",
        purple: "border-purple-500",
        indigo: "border-indigo-500",
        yellow: "border-yellow-500",
        orange: "border-orange-500",
        pink: "border-pink-500",
    };
    const bgColors: Record<string, string> = {
        blue: "bg-blue-50",
        green: "bg-green-50",
        red: "bg-red-50",
        purple: "bg-purple-50",
        indigo: "bg-indigo-50",
        yellow: "bg-yellow-50",
        orange: "bg-orange-50",
        pink: "bg-pink-50",
    };
    const iconColors: Record<string, string> = {
        blue: "text-blue-500",
        green: "text-green-500",
        red: "text-red-500",
        purple: "text-purple-500",
        indigo: "text-indigo-500",
        yellow: "text-yellow-500",
        orange: "text-orange-500",
        pink: "text-pink-500",
    };

    return (
        <div
            className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 border-l-4 ${colorClasses[color]} hover:scale-[1.02] group h-full`}
        >
            <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider truncate">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-0.5 group-hover:scale-105 transition-transform origin-left">
                        {value}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        <p className="text-xs text-gray-500 truncate">{subtitle}</p>
                        {trend && (
                            <span
                                className={`text-xs font-semibold ${trendUp ? "text-green-500" : "text-red-500"} flex items-center gap-0.5`}
                            >
                                {trendUp ? "↑" : "↓"} {trend}
                            </span>
                        )}
                    </div>
                </div>
                <div
                    className={`p-2 rounded-xl ${bgColors[color]} group-hover:scale-110 transition-transform duration-300 group-hover:shadow-lg flex-shrink-0 ml-2`}
                >
                    <div className={`w-5 h-5 ${iconColors[color]}`}>{icon}</div>
                </div>
            </div>
        </div>
    );
};

const SkeletonCard: React.FC = () => (
    <div className="bg-white rounded-2xl shadow-lg p-4 border-l-4 border-gray-200 h-full animate-pulse">
        <div className="h-3 w-20 bg-gray-200 rounded mb-2" />
        <div className="h-6 w-14 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-24 bg-gray-100 rounded" />
    </div>
);

const Panel: React.FC<{
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, subtitle, icon, children }) => (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 border border-gray-100">
        <div className="flex items-center justify-between mb-2">
            <div>
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
            </div>
            {icon}
        </div>
        {children}
    </div>
);

/** Donut + legend, shared by the two breakdown panels. */
const StatusDonut: React.FC<{ data: Array<{ name: string; value: number }> }> = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);

    if (!data.length || total === 0) {
        return <p className="text-xs text-gray-400 py-10 text-center">No data yet</p>;
    }

    const withColor = data.map((item, index) => ({ ...item, color: colorFor(item.name, index) }));

    return (
        <div className="flex items-center justify-center gap-4">
            <div className="relative w-32 h-32">
                <RechartsPieChart width={128} height={128}>
                    <Pie data={withColor} cx={64} cy={64} innerRadius={40} outerRadius={55} paddingAngle={3} dataKey="value">
                        {withColor.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12 }} />
                </RechartsPieChart>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-xl font-bold text-gray-900 leading-none">{total}</span>
                    <span className="text-[10px] text-gray-500 mt-0.5">Total</span>
                </div>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto custom-scrollbar pr-1">
                {withColor.map((item) => (
                    <div key={item.name} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-50 transition-all duration-200">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                        <div>
                            <span className="text-xs text-gray-600">{titleCase(item.name)}</span>
                            <p className="text-sm font-semibold text-gray-900">{item.value}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ---- getting started banner -------------------------------------------------

type SetupKey = "campaigns" | "products" | "events" | "assets";

const SETUP_STEPS: Array<{
    key: SetupKey;
    label: string;
    icon: React.ElementType;
    title: string;
    description: string;
    color: string;
    tint: string;
}> = [
        {
            key: "campaigns",
            label: "campaigns",
            icon: TrendingUp,
            title: "Launch a campaign",
            description: "Create your first campaign from Campaigns & Events.",
            color: "#2563EB",
            tint: "#EFF6FF",
        },
        {
            key: "events",
            label: "events",
            icon: Clock,
            title: "Schedule an event",
            description: "Plan events and connect them to your campaigns.",
            color: "#EA580C",
            tint: "#FFF7ED",
        },
        {
            key: "products",
            label: "products",
            icon: FileText,
            title: "Add your products",
            description: "Register the products your campaigns will promote.",
            color: "#16A34A",
            tint: "#F0FDF4",
        },
        {
            key: "assets",
            label: "assets",
            icon: Database,
            title: "Upload assets",
            description: "Bring in images, videos and documents via Asset Ingestion.",
            color: "#7C3AED",
            tint: "#F5F3FF",
        },
    ];

/** Full class strings (not built dynamically) so Tailwind keeps them in the build. */
const STEP_GRID: Record<number, string> = {
    1: "grid-cols-1 sm:grid-cols-2",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
};

/** ["a"] -> "a", ["a","b"] -> "a or b", ["a","b","c"] -> "a, b or c" */
const joinWithOr = (items: string[]) =>
    items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;

interface GettingStartedNoticeProps {
    tenantName: string;
    counts: Record<SetupKey, number>;
}

/**
 * Getting-started banner. Each step hides once its count is above zero, and
 * the whole banner hides once campaigns, products, events and assets all exist.
 *
 * Colours are set with inline styles on purpose: the global theme restyles
 * Tailwind gradient classes (bg-gradient-*, from-*, to-*) and recolours the
 * text inside them. Inline styles are not affected by those theme rules.
 */
const GettingStartedNotice: React.FC<GettingStartedNoticeProps> = ({ tenantName, counts }) => {
    const pendingSteps = SETUP_STEPS.filter((step) => (counts[step.key] ?? 0) <= 0);
    const completedCount = SETUP_STEPS.length - pendingSteps.length;

    // Everything is set up, so there is nothing left to show.
    if (pendingSteps.length === 0) return null;

    const isFreshWorkspace = completedCount === 0;
    const missingList = joinWithOr(pendingSteps.map((step) => step.label));
    const progressPct = Math.round((completedCount / SETUP_STEPS.length) * 100);

    return (
        <div
            className="mb-4 p-5 rounded-2xl shadow-lg animate-fadeIn"
            style={{
                background: "linear-gradient(120deg, #0A0A0A 0%, #064E2B 55%, #16A34A 100%)",
            }}
        >
            {/* Heading + progress */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div
                        className="p-2.5 rounded-xl flex-shrink-0"
                        style={{
                            backgroundColor: "rgba(255, 255, 255, 0.12)",
                            border: "1px solid rgba(255, 255, 255, 0.22)",
                        }}
                    >
                        <Sparkles className="w-5 h-5" style={{ color: "#FFFFFF" }} />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-base font-semibold" style={{ color: "#FFFFFF" }}>
                            {isFreshWorkspace
                                ? "Your workspace is ready. Let's get started."
                                : "Good progress. Let's finish setting up your workspace."}
                        </h2>
                        <p
                            className="text-xs mt-1 max-w-3xl leading-relaxed"
                            style={{ color: "rgba(255, 255, 255, 0.82)" }}
                        >
                            There are no {missingList} in{" "}
                            <span className="font-semibold" style={{ color: "#FFFFFF" }}>
                                {tenantName}
                            </span>{" "}
                            yet.{" "}
                            {isFreshWorkspace
                                ? "Once your team starts adding content, this dashboard will automatically track views, engagement and ingestion activity."
                                : `Complete the remaining ${pendingSteps.length === 1 ? "step" : "steps"} below to see the full picture of views, engagement and ingestion activity.`}
                        </p>
                    </div>
                </div>

                {!isFreshWorkspace && (
                    <div className="flex-shrink-0 min-w-[140px]">
                        <p className="text-[11px] font-semibold mb-1" style={{ color: "#FFFFFF" }}>
                            {completedCount} of {SETUP_STEPS.length} completed
                        </p>
                        <div
                            className="h-1.5 rounded-full overflow-hidden"
                            style={{ backgroundColor: "rgba(255, 255, 255, 0.25)" }}
                        >
                            <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${progressPct}%`, backgroundColor: "#FFFFFF" }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Remaining steps only */}
            <div className={`grid gap-3 mt-4 ${STEP_GRID[pendingSteps.length]}`}>
                {pendingSteps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                        <div
                            key={step.key}
                            className="flex items-start gap-3 p-3 rounded-xl shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                            style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB" }}
                        >
                            <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: step.tint }}
                            >
                                <Icon className="w-4 h-4" style={{ color: step.color }} />
                            </div>
                            <div className="min-w-0">
                                <p
                                    className="text-[10px] font-semibold uppercase tracking-wider"
                                    style={{ color: step.color }}
                                >
                                    Step {index + 1}
                                </p>
                                <p className="text-xs font-semibold mt-0.5" style={{ color: "#111827" }}>
                                    {step.title}
                                </p>
                                <p className="text-[11px] mt-0.5 leading-snug" style={{ color: "#4B5563" }}>
                                    {step.description}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ---- page -----------------------------------------------------------------

const CustomerAdminDashboard: React.FC<CustomerAdminDashboardProps> = ({ tenant }) => {
    const currentUser = useSelector((state: RootState) => state.auth.user);

    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadDashboard = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError("");

        try {
            const token = localStorage.getItem("accessToken");
            const response = await fetch(`${API_V1_BASE_URL}/dashboard/customer`, {
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            const result = await response.json().catch(() => null);

            if (response.ok && result?.success) {
                setData(result.data as DashboardData);
            } else {
                setError(result?.message || "Unable to load dashboard data.");
            }
        } catch {
            setError("Unable to reach the server. Check your connection and try again.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const stats = data?.stats;
    const tenantName = data?.tenant?.name || tenant?.name || "your organization";

    // Counts for the getting-started banner. The banner decides for itself
    // which steps to show and hides completely once all four are above zero.
    const setupCounts = stats
        ? {
            campaigns: stats.campaigns.total,
            products: stats.products.total,
            events: stats.events.total,
            assets: stats.assets.total,
        }
        : null;

    return (
        <Box className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-4">
            {/* Header */}
            <div className="mb-4 animate-fadeIn">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                            Customer Admin Dashboard
                        </h1>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                            <Activity className="w-3.5 h-3.5" />
                            Welcome back, {currentUser?.firstName || currentUser?.email} — {tenantName}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {data?.generatedAt && (
                            <span className="text-[10px] text-gray-400 hidden sm:inline">
                                Updated {relativeTime(data.generatedAt)}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => loadDashboard(true)}
                            disabled={loading || refreshing}
                            className="px-3 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-medium flex items-center gap-1.5 hover:bg-gray-50 disabled:opacity-50 transition-all"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
                    <TriangleAlert className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-red-700">{error}</p>
                        <button
                            type="button"
                            onClick={() => loadDashboard()}
                            className="text-xs text-red-600 underline mt-1"
                        >
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {/* Alerts */}
            {!!data?.alerts?.length && (
                <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {data.alerts.map((alert) => {
                        const tone =
                            alert.level === "critical"
                                ? "bg-red-50 border-red-200 text-red-700"
                                : alert.level === "warning"
                                    ? "bg-yellow-50 border-yellow-200 text-yellow-700"
                                    : "bg-blue-50 border-blue-200 text-blue-700";
                        return (
                            <div key={alert.code} className={`p-2.5 rounded-xl border flex items-start gap-2 ${tone}`}>
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <p className="text-xs font-medium">{alert.message}</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Getting started: shows only the steps that are still pending */}
            {!loading && setupCounts && <GettingStartedNotice tenantName={tenantName} counts={setupCounts} />}

            {/* Stat cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 mb-4">
                {loading || !stats ? (
                    Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
                ) : (
                    <>
                        <StatCard
                            icon={<TrendingUp className="w-5 h-5" />}
                            title="Total Campaigns"
                            value={stats.campaigns.total}
                            subtitle={`${signed(stats.campaigns.startingThisWeek ?? 0)} starting this week`}
                            color="blue"
                        />
                        <StatCard
                            icon={<FileText className="w-5 h-5" />}
                            title="Total Products"
                            value={stats.products.total}
                            subtitle={`${signed(stats.products.startingThisMonth ?? 0)} starting this month`}
                            color="green"
                        />
                        <StatCard
                            icon={<Clock className="w-5 h-5" />}
                            title="Total Events"
                            value={stats.events.total}
                            subtitle={`${signed(stats.events.startingThisMonth ?? 0)} starting this month`}
                            color="orange"
                        />
                        <StatCard
                            icon={<Database className="w-5 h-5" />}
                            title="Total Assets"
                            value={stats.assets.total}
                            subtitle={`${signed(stats.assets.newThisWeek ?? 0)} this week`}
                            color="purple"
                        />
                        <StatCard
                            icon={<Users className="w-5 h-5" />}
                            title="Internal Users"
                            value={stats.users.active}
                            subtitle={`${stats.users.active} / ${stats.users.max} seats`}
                            color="indigo"
                            trend={`${stats.users.seatsRemaining} free`}
                            trendUp={stats.users.seatsRemaining > 0}
                        />
                        <StatCard
                            icon={<LinkIcon className="w-5 h-5" />}
                            title="Guest Links"
                            value={stats.guestLinks.totalActive}
                            subtitle={
                                stats.guestLinks.expiringIn7Days > 0
                                    ? `${stats.guestLinks.expiringIn7Days} expiring in 7 days`
                                    : "None expiring soon"
                            }
                            color="yellow"
                        />
                        <StatCard
                            icon={<Eye className="w-5 h-5" />}
                            title="Total Views"
                            value={stats.views.total}
                            subtitle={`${stats.views.thisMonth} this month`}
                            color="pink"
                            trend={stats.views.changePct !== null ? `${Math.abs(stats.views.changePct)}%` : undefined}
                            trendUp={(stats.views.changePct ?? 0) >= 0}
                        />
                        <StatCard
                            icon={<Database className="w-5 h-5" />}
                            title="Storage Used"
                            value={formatBytes(stats.storage.usedBytes)}
                            subtitle={`${stats.storage.usedPct}% of ${stats.storage.quotaGb} GB`}
                            color="red"
                        />
                    </>
                )}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                <Panel
                    title="Engagement over time"
                    subtitle="Views, assets & ingestion"
                    icon={<BarChart3 className="w-4 h-4 text-gray-400" />}
                >
                    <div className="h-56">
                        {loading ? (
                            <div className="h-full bg-gray-50 rounded-lg animate-pulse" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data?.engagement || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                    <Tooltip contentStyle={{ fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 10 }} />
                                    <Line type="monotone" dataKey="views" stroke="#3B82F6" strokeWidth={2} dot={{ fill: "#3B82F6", r: 3 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="assets" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981", r: 3 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="ingestion" stroke="#EF4444" strokeWidth={2} dot={{ fill: "#EF4444", r: 3 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </Panel>

                <Panel
                    title="Ingestion this week"
                    subtitle="Processed, failed & in progress"
                    icon={<Activity className="w-4 h-4 text-gray-400" />}
                >
                    <div className="h-56">
                        {loading ? (
                            <div className="h-full bg-gray-50 rounded-lg animate-pulse" />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data?.ingestionWeekly || []}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                    <Tooltip contentStyle={{ fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 10 }} />
                                    <Bar dataKey="processed" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="failed" fill="#EF4444" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="pending" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </Panel>
            </div>

            {/* Breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                <Panel title="Asset Status" subtitle="By approval state" icon={<PieChart className="w-4 h-4 text-gray-400" />}>
                    {loading ? <div className="h-32 bg-gray-50 rounded-lg animate-pulse" /> : <StatusDonut data={data?.assetStatus || []} />}
                </Panel>

                <Panel title="Campaign Status" subtitle="By distribution" icon={<FileText className="w-4 h-4 text-gray-400" />}>
                    {loading ? <div className="h-32 bg-gray-50 rounded-lg animate-pulse" /> : <StatusDonut data={data?.campaignStatus || []} />}
                </Panel>
            </div>

            {/* Activity + storage/billing */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                <Panel title="Recent Activity" subtitle="Latest actions" icon={<Clock className="w-4 h-4 text-gray-400" />}>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
                        {loading ? (
                            Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-8 bg-gray-50 rounded-lg animate-pulse" />)
                        ) : !data?.recentActivity?.length ? (
                            <p className="text-xs text-gray-400 py-8 text-center">No recent activity</p>
                        ) : (
                            data.recentActivity.map((item) => (
                                <div key={item.id} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-gray-50 transition-all duration-200">
                                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                        <UserCheck className="w-3.5 h-3.5 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-gray-700">
                                            <span className="font-semibold">{item.actorName || item.actorEmail}</span>{" "}
                                            {item.details}
                                        </p>
                                        <p className="text-[10px] text-gray-400 mt-0.5">
                                            {titleCase(item.action)} · {relativeTime(item.at)}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Panel>

                <Panel
                    title={data?.billing ? "Storage & Billing" : "Storage"}
                    subtitle={stats ? `${stats.storage.quotaGb} GB limit` : undefined}
                    icon={<Database className="w-4 h-4 text-gray-400" />}
                >
                    {loading || !stats ? (
                        <div className="h-24 bg-gray-50 rounded-lg animate-pulse" />
                    ) : (
                        <>
                            <div className="mb-3">
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="font-medium text-gray-700">{formatBytes(stats.storage.usedBytes)} used</span>
                                    <span className="text-gray-500">
                                        {Math.max(0, Math.round((stats.storage.quotaGb - stats.storage.usedGb) * 100) / 100)} GB remaining
                                    </span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-2 rounded-full transition-all duration-500"
                                        style={{
                                            width: `${Math.min(100, stats.storage.usedPct)}%`,
                                            backgroundColor: stats.storage.usedPct > 80 ? "#EF4444" : "#3B82F6",
                                        }}
                                    />
                                </div>
                                <p className="text-[10px] text-gray-500 mt-0.5">{stats.storage.usedPct}% of quota</p>
                            </div>

                            {/* Billing only renders when the tenant has billing in settings —
                                the schema has no billing tables, so there is nothing to invent. */}
                            {data?.billing && (
                                <>
                                    <Divider className="my-2" />
                                    <div>
                                        <h4 className="text-xs font-semibold text-gray-900 mb-2">Billing Snapshot</h4>
                                        <div className="grid grid-cols-3 gap-2">
                                            <div className="p-2 bg-gray-50 rounded-lg">
                                                <p className="text-[10px] text-gray-500">Plan</p>
                                                <p className="text-xs font-semibold text-gray-900 mt-0.5 truncate">
                                                    {data.billing.plan || "—"}
                                                </p>
                                            </div>
                                            <div className="p-2 bg-gray-50 rounded-lg">
                                                <p className="text-[10px] text-gray-500">Contract</p>
                                                <p className="text-xs font-semibold text-gray-900 mt-0.5">
                                                    {formatMoney(data.billing.contractValue, data.billing.currency)}
                                                </p>
                                            </div>
                                            <div className={`p-2 rounded-lg border ${data.billing.outstanding ? "bg-yellow-50 border-yellow-200" : "bg-gray-50 border-transparent"}`}>
                                                <p className={`text-[10px] ${data.billing.outstanding ? "text-yellow-600" : "text-gray-500"}`}>
                                                    Outstanding
                                                </p>
                                                <p className={`text-xs font-semibold mt-0.5 ${data.billing.outstanding ? "text-yellow-700" : "text-gray-900"}`}>
                                                    {formatMoney(data.billing.outstanding, data.billing.currency)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Panel>
            </div>

            {/* Guest access expiring */}
            <div className="grid grid-cols-1 gap-3">
                <Panel
                    title="Guest Access Expiring"
                    subtitle="Links expiring within 7 days"
                    icon={<AlertCircle className="w-4 h-4 text-yellow-500" />}
                >
                    {loading ? (
                        <div className="h-12 bg-gray-50 rounded-lg animate-pulse" />
                    ) : !data?.guestExpiring?.length ? (
                        <p className="text-xs text-gray-400 py-6 text-center">No guest links expiring in the next 7 days</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {data.guestExpiring.map((guest) => {
                                const days = daysUntil(guest.expiresAt);
                                return (
                                    <div
                                        key={`${guest.source}-${guest.email}-${guest.expiresAt}`}
                                        className="flex items-center justify-between p-2 rounded-lg hover:bg-yellow-50 transition-all duration-200 border border-transparent hover:border-yellow-200"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-blue-600 font-semibold text-xs flex-shrink-0">
                                                {(guest.name || guest.email || "?").charAt(0).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs font-medium text-gray-900 truncate">{guest.name || guest.email}</p>
                                                <p className="text-[10px] text-gray-500 truncate">{guest.context}</p>
                                            </div>
                                        </div>
                                        <Chip
                                            label={days <= 1 ? "1 day" : `${days} days`}
                                            size="small"
                                            className="bg-yellow-100 text-yellow-700 font-medium text-[10px] h-5 flex-shrink-0"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Panel>
            </div>

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 3px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
                .animate-fadeIn { animation: fadeIn 0.3s ease-in-out; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </Box>
    );
};

export default CustomerAdminDashboard;
